import { useField } from 'formik'
import { useMemo, useState } from 'react'
import { DesignWizardField } from '../DesignWizardField'
import { useModals } from 'almanac-ui/Modal'
import { SourceMaterialsModal } from './SourceMaterialsModal'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import Styles from '../DesignWizardField.module.scss'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { KnowledgeBaseSourceMaterial } from 'types'
import { Text } from 'blaze-ui/Text/Text'
import { HighDetailedSource } from 'blaze-ui/icons/24'
import { useOpenSourceMaterialModal } from 'hooks/useOpenSourceMaterialModal'
import { Loading } from '../Loading'

export const SourceMaterialField = () => {
  const { openModal, closeStack } = useModals()
  const sourceMaterialsQuery = useSourceMaterials()
  const { data: sourceMaterials, isLoading } = sourceMaterialsQuery
  const [sourceMaterialIdsField, , { setValue: setSourceMaterialIds }] = useField('source_material_ids')
  const openSourceMaterialModal = useOpenSourceMaterialModal()
  const [resetOrder, setResetOrder] = useState(false)

  const selectedSourceMaterials: KnowledgeBaseSourceMaterial[] =
    sourceMaterials?.filter((sm) => sourceMaterialIdsField.value?.includes(sm.id)) || []

  // Memoize the initial materials so that the don't change order when selecting/unselecting them,
  // we only want to move the selected ones to the top when the modal is submitted
  const initialSelectedMaterials = useMemo(() => selectedSourceMaterials, [resetOrder]) // eslint-disable-line react-hooks/exhaustive-deps
  const otherSourceMaterials = sourceMaterials?.filter((sm) => !initialSelectedMaterials.includes(sm))

  const onHandleSourceMaterialSuccess = () => {
    sourceMaterialsQuery.refetch().then((newSourceMaterials) => {
      setSourceMaterialIds([
        ...sourceMaterialIdsField.value,
        newSourceMaterials.data?.[newSourceMaterials.data.length - 1]?.id,
      ])
    })
    closeStack()
  }

  const openAddSourceMaterialModal = () => {
    openSourceMaterialModal(
      {
        onHandleSourceMaterialSuccess,
      },
      { newStack: true },
    )
  }

  const handlePress = () => {
    if (!sourceMaterials) return

    openModal(
      SourceMaterialsModal,
      {
        initialSourceMaterials: sourceMaterials.filter((sm) => selectedSourceMaterials.includes(sm)),
        sourceMaterials,
        onHandleSourceMaterialSuccess,
        onSourceMaterialsSelect: (sourceMaterials) => {
          setSourceMaterialIds(sourceMaterials.map((sm) => sm.id))
          setResetOrder((value) => !value)
        },
      },
      { newStack: true },
    )
  }

  if (isLoading) return <Loading />

  if (!sourceMaterials?.length)
    return (
      <DesignWizardField
        title="Source Materials"
        isEmpty
        emptyIcon={<HighDetailedSource size={40} />}
        emptyLabel={!sourceMaterials?.length ? 'Create your first Source Material' : 'Select a Source Material'}
        onPress={openAddSourceMaterialModal}
      />
    )

  return (
    <DesignWizardField
      withChecklist
      title="Source Materials"
      className={Styles.sourceMaterial}
      onButtonPress={handlePress}
      buttonLabel="See All Source Materials"
    >
      <>
        {initialSelectedMaterials.map((material: KnowledgeBaseSourceMaterial) => (
          <Checkbox
            size="lg"
            key={material.id}
            className={Styles.checkbox}
            isSelected={selectedSourceMaterials.includes(material)}
            isDisabled={!selectedSourceMaterials.includes(material) && selectedSourceMaterials.length === 3}
            onChange={() => {
              const materialsSet = new Set(selectedSourceMaterials.map((sm) => sm.id))
              materialsSet.has(material.id) ? materialsSet.delete(material.id) : materialsSet.add(material.id)
              setSourceMaterialIds(Array.from(materialsSet).slice(0, 3))
            }}
          >
            <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
              {material.name}
            </Text>
          </Checkbox>
        ))}
        {otherSourceMaterials
          ?.slice(0, 5 - initialSelectedMaterials.length)
          .map((material: KnowledgeBaseSourceMaterial, i) => (
            <Checkbox
              size="lg"
              key={material.id}
              className={Styles.checkbox}
              isSelected={selectedSourceMaterials.includes(material)}
              isDisabled={!selectedSourceMaterials.includes(material) && selectedSourceMaterials.length === 3}
              onChange={() => {
                const materialsSet = new Set(selectedSourceMaterials.map((sm) => sm.id))
                materialsSet.has(material.id) ? materialsSet.delete(material.id) : materialsSet.add(material.id)
                setSourceMaterialIds(Array.from(materialsSet).slice(0, 3))
              }}
            >
              <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
                {material.name}
              </Text>
            </Checkbox>
          ))}
      </>
    </DesignWizardField>
  )
}
